import { LitElement, createRef, css, html, nothing, ref, unsafeHTML, when } from '@lit'
import { storefrontClient } from '@nova/storefront'
import styles from '@nova/styles'
import { formatPrice, t } from '@nova/utils'

class NovaMiniCart extends LitElement {
  static properties = {
    initialItems: {
      type: Array,
    },
    items: {
      type: Array,
      state: true,
    },
    emptyCartCtaText: {
      type: String,
    },
    emptyCartCtaUrl: {
      type: String,
    },
    callOutText: {
      type: String,
    },
    freeShippingData: {
      type: Object,
    },
    compareAtPrices: {
      type: Object,
      state: true,
    },
    continueSell: {
      type: Object,
      state: true,
    },
    maxValues: {
      type: Object,
      state: true,
    }
  }

  get count() {
    const count = this.items.reduce(
      (acc, current) => acc + Number.parseInt(current.quantity),
      0,
    )
    return count
  }

  get subtotal() {
    return this.items.reduce(
      (acc, current) =>
        acc + Number.parseFloat(current.final_price) * current.quantity,
      0,
    )
  }

  get formattedSubtotal() {
    return formatPrice(this.subtotal)
  }

  async queryVariantData(items) {
    const compareAtPrices = {}
    const continueSell = {}
    const maxValues = {}

    await Promise.all(
      items.map(async (item) => {
        const data = await this.getVariantData(item)
        if (!data) return
        compareAtPrices[item.key] = data.compareAtPrice
        continueSell[item.key] = data.inventoryPolicy
        maxValues[item.key] = data.max
      }),
    )

    this.compareAtPrices = compareAtPrices
    this.continueSell = continueSell
    this.maxValues = maxValues
  }

  getVariantData = async (currentItem) => {
    let results
    const matchedItem = this.initialItems.find(
      (item) => item.key === currentItem.key,
    )

    if (matchedItem) {
      results = {
        compareAtPrice: matchedItem.compare_at_price,
        max: matchedItem.inventory_quantity,
        inventoryPolicy: matchedItem.inventory_policy !== 'continue',
      }
    } else {
      const variantData = await storefrontClient.getVariantById(
        currentItem.variant_id,
      )
      if (!variantData) return
      results = {
        compareAtPrice: variantData.compareAtPrice,
        max: variantData.quantityAvailable,
        inventoryPolicy: variantData.currentlyNotInStock,
      }
    }

    return results
  }

  get itemCount() {
    return this.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }

  $cartDrawer = createRef()
  $cartItemCountWrapper = createRef()

  constructor() {
    super()

    document.addEventListener('nova:cart:open', this._handleOpenCart)
    document.addEventListener('nova:cart:close', this._handleCloseCart)
    document.addEventListener('nova:cart:refresh', this.refresh)
    document.addEventListener("discount_kit:cart_changed", this.refresh)
    this.initialItems = []
    this.items = []
  }

  connectedCallback() {
    super.connectedCallback()
    this.emptyCartCtaText = this.getAttribute('empty-cart-cta-text')
    this.emptyCartCtaUrl = this.getAttribute('empty-cart-cta-url')
    this.callOutText = this.getAttribute('call-out-text')
  }

  disconnectedCallback() {
    document.removeEventListener('nova:cart:open', this._handleOpenCart)
    document.removeEventListener('nova:cart:close', this._handleCloseCart)
    document.removeEventListener('nova:cart:refresh', this.refresh)
  }

  _handleOpenCart = () => {
    this.$cartDrawer.value.show()
    this.$cartItemCountWrapper.value.removeAttribute('aria-hidden')
  }

  _handleCloseCart = () => {
    this.$cartDrawer.value.hide()
  }

  refresh = async () => {
    try {
      const response = await fetch('/cart.js')
      if (!response.ok) {
        throw new Error('Error fetching cart')
      }
      const data = await response.json()
      this.items = data.items
      this.queryVariantData(this.items)
      this.$cartItemCountWrapper.value.setAttribute('aria-hidden', true)
    } catch {
      console.error('Error fetching cart')
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initialItems')) {
      this.items = this.initialItems
      this.queryVariantData(this.items)
    }
    if (changedProperties.has('items')) {
      document.dispatchEvent(
        new CustomEvent('nova:cart:items-count', {
          detail: { count: this.count },
        }),
      )
      document.dispatchEvent(
        new CustomEvent('nova:changeQuantityFinished'), {
        bubbles: true,
      })
    }
  }

  get callOutSection() {
    const div = document.createElement("div")
    div.classList.add("call-out-container")
    div.innerHTML = this.callOutText
    return div
  }

  _renderEmptyState = () => {
    return html`
      <div class="empty-state">
        <p>${t('cart.general.empty')}</p>
        <a href=${this.emptyCartCtaUrl} class="btn btn--primary">
          ${unsafeHTML(this.emptyCartCtaText)}
        </a>
      </div>
    `
  }
  continue_shopping
  _renderItems = () => {
    return html`
      ${__NOVA_GLOBAL__.showCartProgressBar === true
        ? html`<nova-free-shipping-threshold .freeShippingData=${this.freeShippingData}></nova-free-shipping-threshold>`
        : nothing
      }
      ${this.callOutText
        ? this.callOutSection
        : nothing
      }
      <div class="items" part="items">
        ${this.items.map(
          (item) =>
            html`
              <nova-cart-item
                @nova-change=${this.refresh}
                .item=${item}
                .compareAtPrice=${this.compareAtPrices[item.key] || nothing}
                .inventoryPolicy=${this.continueSell[item.key] || nothing}
                .max=${this.maxValues[item.key] || nothing}
              >
              </nova-cart-item>
            `,
        )}
      </div>
      <!-- BEAM START -->
      <div data-beam-widget="select-nonprofit"  data-cart-empty="true" style="padding:20px 20px; border-top: 0.820513px solid rgb(182, 170, 155);">
        <beam-select-nonprofit
          apiKey="pTGFJhV8Ll7U.31ace206-a512-4997-bc68-221745d6510b"
          storeId="437"
          lang="en"
        ></beam-select-nonprofit>
      </div>
      <!-- BEAM END -->
      <div slot="footer" class="summary">
        ${this.itemCount
          ? html`<div class="summary__item-count">
              <p>${t('cart.general.order_summary')}</p>
              <p>${this.itemCount} ${t('cart.general.items')}</p>
            </div>`
          : nothing
        }
        <div class="summary__total">
          <p>${t('cart.general.subtotal')}</p>
          <p>${this.formattedSubtotal}</p>
        </div>
        <sl-button href="/checkout">${t('cart.general.checkout')}</sl-button>
      </div>
    `
  }

  render() {
    return html`
      <sl-drawer class="drawer" ${ref(
        this.$cartDrawer,
    )}>
      <div slot="label" ${ref(
        this.$cartItemCountWrapper,
      )}>
        <span>${t('cart.general.title')}</span>
       ${this.itemCount
          ? html`<span class="header__item-count">(${this.itemCount})</span>`
          : nothing
        }
      </div>
        ${when(!this.items.length, this._renderEmptyState, this._renderItems)}
      </sl-drawer>
    `
  }

  static styles = [
    styles.flex,
    css`
      :host {
        display: block;
      }
      .drawer {
        --size: 100%;
        --header-spacing: 30px 30px 20px 30px;
        --body-spacing: 0px 30px;
        --footer-spacing: 0;
      }
      @media screen and (min-width: 430px) {
        .drawer {
          --size: 430px;
        }
      }
      .drawer::part(base) {
        z-index: var(--nova-z-index-navigation);
      }
      .drawer::part(overlay) {
        fill: var(--nova-utility-overlay, rgba(0, 0, 0, 0.5));
      }
      .drawer::part(panel) {
        background-color: var(--nova-color-brand-beige);
        overflow: hidden;
      }
      .drawer::part(header) {
        align-items: baseline;
        border-bottom: 1px solid var(--nova-color-brand-taupe);
      }
      .header__item-count {
        font-size: 18px;
        font-weight: 400;
        font-family: var(--nova-font-italic-primary);
      }
      .drawer::part(title) {
        text-transform: uppercase;
        font-family: var(--nova-font-bold);
        font-size: 18px;
        line-height: 130%;
        font-weight: 700;
        padding: 20px;
      }
      .drawer::part(header-actions) {
        padding: var(--nova-space-sm);
      }
      .drawer::part(close-button) {
        width: 16px;
        height: 16px;
      }
      .drawer::part(close-button__base) {
        color: black;
        padding: 0;
        font-size: 20px;
      }
      .drawer::part(body) {
        padding: 0;
      }
      .empty-state {
        display: flex;
        row-gap: var(--nova-space-sm);
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        padding: var(--nova-space-xl) var(--nova-space-sm);
      }
      @media screen and (min-width: 768px) {
        .empty-state {
          padding-top: var(--nova-space-lg);
        }
      }
      .empty-state p {
        font-size: 14px;
        margin: 0;
      }
      sl-button,
      a.btn {
        display: flex;
      }
      sl-button::part(base),
      a.btn {
        background-color: var(--nova-color-white);
        color: var(--nova-color-brand-charcoal);
        min-height: 35px;
        width: 100%;
        border: none;
        border-radius: var(--nova-border-rounded);
        align-items: center;
        font: var(--nova-h8-font);
        text-transform: uppercase;
        transition: background-color 0.2s, color 0.2s, border-color 0.2s;
      }
      a.btn {
        padding-inline: var(--nova-button-padding-x);
        width: auto;
        text-decoration: none;
      }
      sl-button::part(base):hover,
      a.btn:hover {
        background-color: var(--nova-color-brand-charcoal);
        color: var(--nova-color-white);
      }
      .call-out-container {
        padding: var(--nova-space-sm);
        border-bottom: 1px solid var(--nova-color-brand-taupe);
      }
      .call-out-container :where(h1, h2, h3, h4, h5, h6, p) {
        font: var(--nova-h7-font);
        text-transform: uppercase;
        margin: 0;
      }
      .call-out-container em {
        font-weight: 400;
        font-family: var(--nova-font-italic-primary);
      }
      .call-out-container a {
        color: inherit;
      }
      .items {
        padding-top: 20px;
      }
      .summary {
        border-top: 1px solid var(--nova-color-brand-taupe);
        padding: var(--nova-space-sm);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
      }
      .summary__total,
      .summary__item-count {
        display: flex;
        justify-content: space-between;
      }
      .summary__item-count {
        margin-bottom: var(--nova-space-xsm);
      }
      .summary__total {
        margin-bottom: var(--nova-space-sm);
      }
      .summary__total p,
      .summary__item-count p {
        margin: 0;
      }
      .summary sl-button::part(base) {
        background-color: var(--nova-color-brand-charcoal);
        color: var(--nova-color-white);
        text-transform: uppercase;
      }

      /* HACK: The main font family 'HelveticaNeueETPro-75-Bold' renders the ® symbol on the baseline instead of raising it like a superscript.
        Apply a hack to visually raise the ® symbol and make it appear as a true superscript. */
      .superscript {
        /* HACK: Need to enforce superscript for heading font family */
        font-size: 50% !important;
        vertical-align: super;
      }

      em .superscript {
        font-size: inherit !important;
        vertical-align: baseline;
        font-style: italic;
      }
    `,
  ]
}

customElements.define('nova-mini-cart', NovaMiniCart)
